import React from 'react';
import { InputNumber, Space, Switch, Table, Typography } from 'antd';
import SeriesPasteEditor from '../SeriesPasteEditor';
import { PricingChartPreview } from '../PagePreview';
import RichField from '../RichField';
import { Help, Panel, S } from '../ui';

const { Text } = Typography;

interface Props {
  source: any;
  patch: (updater: (next: any) => void) => void;
  derived: any;
}

const TIERS = [
  { key: 't1', label: 'T1' },
  { key: 't2', label: 'T2' },
  { key: 't3', label: 'T3' },
] as const;

/**
 * The price index: one monthly table, plus the month-on-month changes.
 *
 * The changes are a separate field rather than a calculation because ClearBlue supply
 * them at full precision and we store prices rounded to the cent. Recomputing from the
 * rounded values gives a different number -- for June 2026 that is -0.11% against a
 * published -0.06% on T1 -- and it is the supplied figure that gets published. So the
 * page shows both: what the stored prices imply, and what was actually supplied, with the
 * difference visible rather than silently resolved one way or the other.
 */
const StepPricing: React.FC<Props> = ({ source, patch, derived }) => {
  const monthly = source?.pricing?.monthly ?? { periods: [], t1: [], t2: [], t3: [] };
  const precise = source?.pricing?.precise_change_pct ?? null;
  const usingSupplied = precise !== null;

  const setMonthly = (periods: string[], columns: number[][]) =>
    patch((next) => {
      next.pricing.monthly = { periods, t1: columns[0], t2: columns[1], t3: columns[2] };
    });

  /** What the stored, rounded prices imply, for comparison with the supplied figure. */
  const impliedChange = (tier: 't1' | 't2' | 't3'): number | null => {
    const values: number[] = monthly[tier] ?? [];
    if (values.length < 2) return null;
    const previous = values[values.length - 2];
    const current = values[values.length - 1];
    if (!previous) return null;
    return ((current - previous) / previous) * 100;
  };

  const rows = TIERS.map(({ key, label }) => {
    const implied = impliedChange(key);
    const supplied = precise?.[key];
    const differs =
      usingSupplied && implied !== null && typeof supplied === 'number' &&
      Math.abs(implied - supplied) >= 0.005;
    return { key, label, implied, supplied, differs };
  });

  return (
    <div>
      <Help>
        From the ClearBlue workbook you added in step 1. Correct any figure directly in the
        table; the chart below redraws as you type.
      </Help>

      <SeriesPasteEditor
        title="Monthly prices"
        help="Month by month, one column per rating tier."
        placeholder={'2026-06\t9.11\t7.91\t6.72\n2026-07\t9.12\t7.93\t6.74'}
        kind="monthly"
        valueLabels={['T1', 'T2', 'T3']}
        periods={monthly.periods ?? []}
        columns={[monthly.t1 ?? [], monthly.t2 ?? [], monthly.t3 ?? []]}
        onChange={setMonthly}
      />

      <Panel
        title="Month-on-month change"
        aside={
          <Space size={S.sm}>
            <Text type="secondary" style={{ fontSize: 12 }}>Use the supplied figures</Text>
            <Switch
              size="small"
              checked={usingSupplied}
              onChange={(on) =>
                patch((next) => {
                  next.pricing.precise_change_pct = on
                    ? {
                        t1: Number((impliedChange('t1') ?? 0).toFixed(2)),
                        t2: Number((impliedChange('t2') ?? 0).toFixed(2)),
                        t3: Number((impliedChange('t3') ?? 0).toFixed(2)),
                      }
                    : null;
                })
              }
            />
          </Space>
        }
      >
        <Help>
          ClearBlue calculate these from unrounded prices, so they differ slightly from
          anything worked out from the table above. Turn the switch off to use the table
          instead, and the three cards on the page will say so.
        </Help>

        <Table
          size="small"
          rowKey="key"
          pagination={false}
          style={{ maxWidth: 620 }}
          dataSource={rows}
          columns={[
            { title: 'Tier', dataIndex: 'label', key: 'label', width: 80 },
            {
              title: 'From the table above',
              key: 'implied',
              width: 180,
              render: (_: unknown, row: any) => (
                <Text type="secondary">
                  {row.implied === null ? 'needs two months' : `${row.implied.toFixed(2)}%`}
                </Text>
              ),
            },
            {
              title: 'Supplied by ClearBlue',
              key: 'supplied',
              render: (_: unknown, row: any) =>
                usingSupplied ? (
                  <Space size={S.xs}>
                    <InputNumber
                      size="small"
                      step={0.01}
                      style={{ width: 110 }}
                      value={row.supplied}
                      onChange={(v) =>
                        patch((next) => {
                          next.pricing.precise_change_pct = {
                            ...(next.pricing.precise_change_pct ?? { t1: 0, t2: 0, t3: 0 }),
                            [row.key]: v ?? 0,
                          };
                        })
                      }
                    />
                    <Text type="secondary">%</Text>
                    {row.differs && (
                      <Text type="warning" style={{ fontSize: 12 }}>
                        differs from the table, which is expected
                      </Text>
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">not used</Text>
                ),
            },
          ]}
        />
      </Panel>

      <Panel title="How the chart will look">
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: S.sm }}>
          Monthly and annual, as the page draws them. The annual series is the mean of the
          months in each year and is never typed.
        </Text>
        <PricingChartPreview derived={derived} />
      </Panel>

      {/*
        Both pieces of writing that appear on this half of the page, in the order the page
        prints them: the italic note sits under the chart, the summary beside it.
      */}
      <RichField
        label="The italic note under the price chart"
        help="The standing note about how the index is maintained, and how many projects it covers. Written out in full, as one paragraph."
        editorKey={`note-${source?.as_of ?? 'blank'}`}
        value={source?.commentary?.note ?? ''}
        onChange={(html) => patch((next) => { next.commentary.note = html; })}
      />

      <RichField
        label="Summary beside the price chart"
        help="What prices did across the three tiers. The figures above are the ones it should quote."
        editorKey={`pricing-${source?.as_of ?? 'blank'}`}
        value={source?.commentary?.pricing ?? ''}
        onChange={(html) => patch((next) => { next.commentary.pricing = html; })}
      />

      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
        En dashes and em dashes are not used in Calyx copy, and saving will say so if one
        gets in.
      </Text>
    </div>
  );
};

export default StepPricing;
